<template>
    <div class="template-selection-card">
        <div class="p-3">
            <div class="search-wrapper">
                <i class="ri-search-line"></i>
                <input type="text" ref="searchbox" class="search-input" placeholder="Buscar..." v-model="search" autofocus>
            </div>
        </div>

        <div class="template-list custom-scrollbar">
            <div v-for="item in filteredTemplates" :key="item.template.id" class="template-item" tabindex="0" role="button" @click="selectTemplate(item)" @keydown.enter.prevent="selectTemplate(item)">
                <div v-if="item.type == 'whiz-template'" class="template-icon" :class="getIconClass(item.template.conteudo!)">
                    <i :class="getIcon(item.template.conteudo!)"></i>
                </div>
                <div v-else class="template-icon" :class="getMetaIconClass(item.template)">
                    <i :class="getMetaIcon(item.template)"></i>
                </div>
                <div class="template-info">
                    <!-- As duas famílias convivem na mesma lista e se comportam de forma
                         diferente: o oficial sai pela Meta (vale fora da janela de 24h, pode
                         pedir variáveis e ser recusado por lá), o do Whiz é texto nosso. Sem
                         o selo, só o ícone distinguia — e ele fala do tipo de conteúdo. -->
                    <template v-if="item.type == 'whiz-template'" >
                        <span class="template-name">
                            {{ item.template.nome }}
                            <span class="selo-origem selo-whiz">{{ branding.shortName }}</span>
                        </span>
                        <span class="template-preview">{{ getPreviewText(item.template) }}</span>
                    </template>
                    <template v-else>
                        <span class="template-name">
                            {{ item.template.displayName }}
                            <span class="selo-origem selo-meta"><i class="ri-meta-line"></i> Meta</span>
                            <!-- Reclassificado: a Meta aprovou em outra categoria (e cobra por ela). -->
                            <i v-if="ehReclassificado(item.template)" class="ri-arrow-left-right-line icone-reclassificado"
                                v-tooltip="descricaoReclassificacao(item.template)"></i>
                        </span>
                        <span class="template-preview">
                            {{ getMetaPreviewText(item.template) }}<template v-if="seloDaCategoria(item.template.category)"> · {{ seloDaCategoria(item.template.category)!.label }}</template>
                        </span>
                    </template>
                </div>
                <i class="ri-arrow-right-s-line text-muted"></i>
            </div>

            <div v-if="filteredTemplates.length === 0" class="empty-state">
                <i class="ri-layout-masonry-line mb-2"></i>
                <span>Nenhum template encontrado</span>
            </div>
        </div>

        <div class="card-footer" @click="openManager">
            <i class="ri-settings-3-fill me-2"></i> Gerenciar Templates
        </div>
    </div>
</template>

<script setup lang="ts">
import { branding } from '@/config/branding';
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useUiStore } from '@/stores/app/uiStore';
import { ResponseTemplate } from '@/services/templates/templates.service';
import { useAuthStoreUser } from '@/stores/app/authUser';
import { useMultiTemplateStore } from '@/stores/app/templateStore';
import { FetchMetaTemplates } from '@/utils/fetch-meta-templates.composable';
import { useChatStore } from '@/stores/app/chatStore';
import { IMetaTemplate, MetaTemplatesService } from '@/services/templates-wpp-meta/meta-wpp-template.service';
import { StrContainsNFDNormalized } from '@/utils/strContains';
import { delay } from '@/utils/delay';
import { seloDaCategoria } from '@/services/templates-wpp-meta/template-categories';
import { descricaoReclassificacao, ehReclassificado } from '@/services/templates-wpp-meta/meta-template-edicao';
import { confirmarEnvioDeReclassificado } from '@/components/app/swal/ConfirmarEnvioReclassificado';

const authStore = useAuthStoreUser();
const chatStore = useChatStore();
const currentContaId = computed(() => authStore.currentConta?.id)
const templateStore = useMultiTemplateStore();
watch(() => authStore.initialized, (isInit) => isInit
    ? templateStore.init(authStore.contasFull.map(c => c.id))
    : null
);
const currentContaTemplates = computed(() => currentContaId.value ? templateStore.contasTemplatesMap.get(currentContaId.value) ?? [] : []);
const { officialTemplatesDoChatAtivo } = FetchMetaTemplates(chatStore);

const emit = defineEmits({
    close() { return true },
    manage() { return true },
    selectWhizTemplate(r: ResponseTemplate) { return r },
    selectMetaTemplate(m: IMetaTemplate) { return m },
});
const uiStore = useUiStore();
const search = ref('');

type ItemTemplate =
    | { type: 'meta-template'; template: IMetaTemplate }
    | { type: 'whiz-template'; template: ResponseTemplate };

const filteredTemplates = computed(() => {
    // Inbox com metade Cloud API (inclusive híbrido): janela aberta mostra Meta + Whiz,
    // janela fechada mostra só os oficiais (é o único conteúdo que sai fora dela).
    const modo = chatStore.modoTemplatesRapidos;
    // Só os templates do inbox dono do atendimento: um template Meta vive no
    // WABA de um inbox e a Meta recusa o envio por qualquer outro.
    const metaAprovados = officialTemplatesDoChatAtivo.value
        .filter(t => t.status == 'APPROVED')
        .map(t => ({ type: 'meta-template' as const, template: t }));
    const whiz = currentContaTemplates.value.map(t => ({ type: 'whiz-template' as const, template: t }));

    const list: ItemTemplate[] = modo === 'SO_META' ? metaAprovados
        : modo === 'META_E_WHIZ' ? [...metaAprovados, ...whiz]
            : whiz;

    if (!search.value) return list;

    const q = search.value;
    return list.filter(t => t.type == 'whiz-template' ? StrContainsNFDNormalized(q, t.template.nome) || StrContainsNFDNormalized(q, t.template.mensagem) : StrContainsNFDNormalized(q, t.template.displayName) || StrContainsNFDNormalized(q, t.template.bodyJson));
});

function openManager() { emit('manage'); uiStore.openTemplatesModal(); }

function getIcon(type: string) {
    if (type === 'PIX') return 'ri-pix-fill';
    if (type === 'SENHA_USO_UNICO') return 'ri-shield-keyhole-line';
    if (type === 'ANEXO') return 'ri-file-text-line';
    return 'ri-chat-3-line';
}

function getIconClass(type: string) {
    if (type === 'PIX') return 'icon-pix';
    if (type === 'SENHA_USO_UNICO') return 'icon-pass';
    if (type === 'ANEXO') return 'icon-file';
    return 'icon-text';
}

function getPreviewText(item: ResponseTemplate) {
    if (item.conteudo === 'PIX') return 'Chave Pix';
    if (item.conteudo === 'SENHA_USO_UNICO') return 'Senha Segura';
    return item.mensagem;
}

const getTemplateTextMeta       = MetaTemplatesService.getTemplateTextMeta;
const getIconColorClassMeta     = MetaTemplatesService.getIconColorClassMeta;
const getTemplateTypeIconMeta   = MetaTemplatesService.getTemplateTypeIconMeta;

function getMetaIcon(item: IMetaTemplate) {
    return getTemplateTypeIconMeta(item.headerType);
}

function getMetaIconClass(item: IMetaTemplate) {
    return getIconColorClassMeta(item.headerType);
}

function getMetaPreviewText(item: IMetaTemplate) {
    return getTemplateTextMeta(item.headerType)
}

async function selectTemplate(item: { type: 'meta-template', template: IMetaTemplate } | { type: 'whiz-template', template: ResponseTemplate}) {
    switch (item.type) {
        case 'meta-template':
            // Reclassificado: confirma (1x por usuário e template) antes de seguir para o envio.
            if (!(await confirmarEnvioDeReclassificado(item.template, authStore.user?.id))) return;
            return emit('selectMetaTemplate', item.template)
        case 'whiz-template': return emit('selectWhizTemplate', item.template)
    }
}

type FocusableElement = Element & { focus: () => unknown };
let focusedEl: null | FocusableElement = null;
const searchbox = ref<FocusableElement | null>(null);
onMounted(async () => { focusedEl = document.activeElement as FocusableElement; await delay(100); searchbox.value?.focus(); });
onUnmounted(() => focusedEl && document.contains(focusedEl) ? focusedEl?.focus() : null)
</script>

<style scoped>
.template-selection-card {
    width: 340px; max-height: 480px;
    background: var(--bs-light); border-radius: 16px;
    box-shadow: 0 10px 40px rgba(0,0,0,0.15);
    display: flex; flex-direction: column; overflow: hidden;
    border: 1px solid rgba(0,0,0,0.08);
}

.card-header {
    padding: 16px;
    display: flex; justify-content: space-between; align-items: center;
}
.card-header h6 { margin: 0; font-weight: 700; }
.btn-icon-close { background: none; border: none; font-size: 20px; color: #999; cursor: pointer; }

.search-wrapper {
    background: var(--bs-white); border-radius: 8px; padding: 8px 12px;
    display: flex; align-items: center; gap: 8px; color: #666;
}
.search-input { border: none; background: transparent; width: 100%; outline: none; font-size: 14px; }

.template-list { flex: 1; overflow-y: auto; }

.template-item {
    padding: 12px 16px; display: flex; align-items: center; gap: 12px;
    cursor: pointer; transition: background 0.2s;
}
.template-item:hover,
.template-item:focus { background-color: #5ca8ff1c; }
.template-item:focus { outline: 2px solid var(--bs-primary); outline-offset: -2px; }
.template-item:focus-visible { outline: 2px solid var(--bs-primary); outline-offset: -2px; }

.icone-reclassificado { color: #ef6820; font-size: 13px; margin-left: 4px; vertical-align: middle; }

.template-icon {
    width: 40px; height: 40px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    font-size: 18px; flex-shrink: 0;
}
.icon-text {
    background: var(--bs-white);
    color: #666;
}

.icon-pix {
    background: #00dbcf1f;
    color: #32bcad;
}

.icon-pass {
    background: #0024ff1f;
    color: #3f51b5;
}

.icon-file {
    background: #ff9c0017;
    color: #ff9800;
}


.template-info { flex: 1; overflow: hidden; display: flex; flex-direction: column; }
.template-name { font-weight: 600; font-size: 14px; display: flex; align-items: center; gap: 6px; min-width: 0; }
.template-name > :first-child { overflow: hidden; text-overflow: ellipsis; }

/* Selo de origem: discreto de propósito — informa, não compete com o nome. */
.selo-origem {
    flex-shrink: 0;
    font-size: 9.5px; font-weight: 700; line-height: 1;
    letter-spacing: .3px; text-transform: uppercase;
    padding: 3px 6px; border-radius: 999px; border: 1px solid transparent;
}
.selo-meta { background: rgba(24, 119, 242, .12); color: #1877f2; border-color: rgba(24, 119, 242, .25); }
.selo-whiz { background: rgba(100, 116, 139, .12); color: #64748b; border-color: rgba(100, 116, 139, .22); }
[data-bs-theme="dark"] .selo-meta { background: rgba(59, 130, 246, .18); color: #93c5fd; border-color: rgba(59, 130, 246, .35); }
[data-bs-theme="dark"] .selo-whiz { background: rgba(148, 163, 184, .16); color: #cbd5e1; border-color: rgba(148, 163, 184, .3); }
.template-preview { font-size: 12px; color: #888; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.empty-state { text-align: center; padding: 30px; color: #999; display: flex; flex-direction: column; align-items: center; font-size: 13px; }
.empty-state i { font-size: 24px; }

.card-footer {
    padding: 12px; text-align: center; background: rgba(var(--bs-primary-rgb), 0.08);
    color: var(--bs-primary); font-weight: 600; font-size: 13px; cursor: pointer;
    transition: background 0.2s;
}
.card-footer:hover { background: rgba(var(--bs-primary-rgb), 0.21);}

.custom-scrollbar::-webkit-scrollbar { width: 5px; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #ddd; border-radius: 10px; }
</style>
